import React, { useEffect, useMemo, useState } from "react";
import api from "../api/client";
import { usePublishScreenData } from "../utils/aiScreenContext";
import IntegrationCollector from "../components/IntegrationCollector";
import ActionButton from "../components/ActionButton";
import { useCustomer } from "../context/CustomerContext";
import { useSort, sortRows, SortableTh } from "../components/SortableTh";
import { formatDateTime, utcMillis } from "../utils/datetime";
import { backdropClose } from "../components/ModalBackdrop";

type Customer = { id: number; name: string };
type Device = { id: number; hostname: string; customer_id: number; status: string };
type ScanTarget = {
  id: number;
  customer_id: number;
  customer_name?: string | null;
  label?: string | null;
  cidr: string;
  scanner_device_id?: number | null;
  scanner_hostname?: string | null;
  enabled: boolean;
  scan_interval_minutes: number;
  portscan_enabled: boolean;
  portscan_ports: string;
  snmp_autodetect_enabled: boolean;
  max_parallel: number;
  last_scan_started_at?: string | null;
  last_scan_completed_at?: string | null;
  last_scan_status?: string | null;
  last_scan_error?: string | null;
  last_discovered_count?: number | null;
};
type Discovered = {
  id: number;
  customer_id: number;
  customer_name?: string | null;
  ip_address: string;
  mac_address?: string | null;
  hostname?: string | null;
  vendor?: string | null;
  os_guess?: string | null;
  open_ports?: number[] | null;
  snmp_info?: Record<string, any> | null;
  device_class: string;
  class_overridden?: boolean;
  managed_device_id?: number | null;
  promoted_network_device_id?: number | null;
  ignored_at?: string | null;
  first_seen_at?: string;
  last_seen_at: string;
  seen_count: number;
  // Computed status (hover peek-card).
  agent_status?: string | null;
  agent_version?: string | null;
  agent_last_seen?: string | null;
  deploy_status?: string;
  deploy_error?: string | null; // the plain sentence, not the transport log
  deploy_cause?: string | null; // stable slug: rpc_unreachable, access_denied, …
  deploy_fix?: string | null; // what to actually do about it
  deploy_log?: string | null; // the verbatim log, for the drawer
  last_attempt_at?: string | null;
};

// Full record returned by GET /discovered/{id} for the detail drawer.
type DiscoveredDetail = Discovered & {
  agent_hostname?: string | null;
  deploy_command_id?: number | null;
  deploy_command_status?: string | null;
  deploy_exit_code?: number | null;
  deploy_stdout?: string | null;
  deploy_stderr?: string | null;
  deploy_attempted_by?: string | null;
  deploy_credentialed?: boolean | null;
  ignore_reason?: string | null;
  last_install_attempt?: Record<string, any> | null;
};
type Vendor = {
  vendor: string;
  label: string;
  needs_base_url: boolean;
  extra_fields: {
    key: string;
    label: string;
    type: string;
    required?: boolean;
    help?: string;
  }[];
  /** Vendor-specific note for the shared credential box, which is labelled
   *  generically ("API key / token / password") and so cannot tell the operator
   *  of any one vendor what to actually paste. */
  credential_help?: string | null;
};
type Integration = {
  id: number;
  customer_id: number;
  customer_name?: string | null;
  vendor: string;
  name: string;
  api_base_url?: string | null;
  extra_config: Record<string, any>;
  enabled: boolean;
  has_api_key: boolean;
  last_sync_at?: string | null;
  last_sync_status?: string | null;
  last_sync_error?: string | null;
  /** DERIVED server-side: ok | stale | failing | disabled | never.
   *  Key the pill off THIS, not last_sync_status — a "success" from three
   *  months ago is not health. */
  sync_health?: string | null;
  last_device_count?: number | null;
  sync_interval_minutes: number;
  /** Run the sync from an agent on the customer's LAN instead of the appliance. */
  poll_via_agent?: boolean;
  collector_device_id?: number | null;
  last_collector_device_id?: number | null;
  last_collector_hostname?: string | null;
  /** False for cloud vendors, which the appliance reaches directly. */
  collector_supported?: boolean;
};
type DeployCred = {
  configured: boolean;
  username?: string | null;
  domain?: string | null;
};

const CLASS_LABEL: Record<string, string> = {
  unmanaged_windows: "Unmanaged Windows",
  unmanaged_nix: "Unmanaged Linux/Unix",
  server: "Server",
  network_gear: "Network gear",
  switch: "Switch",
  router: "Router",
  firewall: "Firewall",
  access_point: "Access point",
  nas: "NAS / storage",
  printer: "Printer",
  iot: "IoT / appliance",
  already_managed: "Already managed",
  already_snmp: "Already SNMP-monitored",
  unknown: "Unknown",
};
const CLASS_COLOR: Record<string, string> = {
  unmanaged_windows: "bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300",
  unmanaged_nix: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300",
  server: "bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300",
  network_gear: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  switch: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  router: "bg-purple-100 text-purple-800 dark:bg-purple-900/40 dark:text-purple-300",
  firewall: "bg-rose-100 text-rose-800 dark:bg-rose-900/40 dark:text-rose-300",
  access_point: "bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-300",
  nas: "bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300",
  printer: "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-200",
  iot: "bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300",
  already_managed: "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300",
  already_snmp: "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300",
  unknown: "bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300",
};
// Classes an admin may pick + which count as network gear (SNMP-promotable).
const MANUAL_CLASSES = [
  "unmanaged_windows",
  "unmanaged_nix",
  "server",
  "network_gear",
  "switch",
  "router",
  "firewall",
  "access_point",
  "printer",
  "nas",
  "iot",
  "unknown",
];
/**
 * Map a vendor model string to a friendly category label. Today we have
 * good coverage of the Meraki product line (the dominant case in the
 * Discovered list since they're the main vendor pulled via API). Other
 * vendors just fall through to "Network gear" — extend the lookup if a
 * second vendor's catalog starts cluttering the table.
 *
 * Examples:
 *   MX67  → "Security appliance"
 *   MR36  → "Access point"
 *   MS120-8 → "Switch"
 *   MV12  → "Camera"
 *   MG21  → "Cellular gateway"
 *   MT10  → "Sensor"
 *   Z3    → "Teleworker gateway"
 */
function modelCategoryLabel(model: string | null | undefined): string | null {
  if (!model) return null;
  const m = model.trim().toUpperCase();
  if (m.startsWith("MX")) return "Security appliance";
  if (m.startsWith("MR")) return "Access point";
  if (m.startsWith("MS")) return "Switch";
  if (m.startsWith("MV")) return "Camera";
  if (m.startsWith("MG")) return "Cellular gateway";
  if (m.startsWith("MT")) return "Sensor";
  if (/^Z\d/.test(m)) return "Teleworker gateway";
  // Cisco non-Meraki — keep things short, fall through to nothing.
  return null;
}

// Only unmanaged endpoints can take the agent installer — network gear,
// printers, already-managed, etc. are not push-installable.
const isInstallable = (d: Discovered) =>
  d.device_class === "unmanaged_windows" || d.device_class === "unmanaged_nix";

// Deploy lifecycle → label + colored dot. Tuned to read well in light AND dark.
const DEPLOY_STATUS_META: Record<
  string,
  { label: string; dot: string; text: string }
> = {
  installed: { label: "Agent installed", dot: "bg-green-500", text: "text-green-700 dark:text-green-300" },
  deployed: { label: "Deployed — awaiting check-in", dot: "bg-teal-500", text: "text-teal-700 dark:text-teal-300" },
  in_progress: { label: "Install in progress", dot: "bg-amber-500 animate-pulse", text: "text-amber-700 dark:text-amber-300" },
  attempted: { label: "Install attempted", dot: "bg-amber-400", text: "text-amber-700 dark:text-amber-300" },
  failed: { label: "Deploy failed", dot: "bg-red-500", text: "text-red-700 dark:text-red-300" },
  snmp_monitored: { label: "SNMP monitored", dot: "bg-emerald-500", text: "text-emerald-700 dark:text-emerald-300" },
  ignored: { label: "Ignored", dot: "bg-gray-400", text: "text-gray-500 dark:text-gray-400" },
  never: { label: "No agent", dot: "bg-gray-300 dark:bg-gray-600", text: "text-gray-500 dark:text-gray-400" },
};
const deployMeta = (s?: string) => DEPLOY_STATUS_META[s || "never"] || DEPLOY_STATUS_META.never;

const fmtWhen = (s?: string | null) => (s ? new Date(s).toLocaleString() : "—");

// Status dot + label with a hover peek-card (agent state OR deploy error),
// mirroring how NinjaOne/Datto surface device state on hover.
function StatusPill({ d }: { d: Discovered }) {
  const m = deployMeta(d.deploy_status);
  const online = d.agent_status === "online";
  return (
    <div className="relative inline-flex group">
      <span className="inline-flex items-center gap-1.5 cursor-default">
        <span className={`w-2 h-2 rounded-full ${m.dot}`} />
        <span className={`text-xs font-medium ${m.text}`}>{m.label}</span>
      </span>
      {/* hover peek-card */}
      <div
        className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 hidden group-hover:block
          w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900
          shadow-xl p-3 text-xs text-gray-700 dark:text-gray-200"
      >
        <div className="flex items-center gap-1.5 font-semibold mb-1">
          <span className={`w-2 h-2 rounded-full ${m.dot}`} />
          <span className={m.text}>{m.label}</span>
        </div>
        {d.deploy_status === "installed" ? (
          <div className="space-y-0.5">
            <div>Agent: <span className={online ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>{d.agent_status || "unknown"}</span></div>
            <div>Version: {d.agent_version || "—"}</div>
            <div>Last check-in: {fmtWhen(d.agent_last_seen)}</div>
          </div>
        ) : d.deploy_error ? (
          /* WAS a monospace dump of the first 240 characters of a
             three-transport PowerShell log. Across 1,578 measured attempts
             the loudest passage in that text is usually the least relevant
             one — a WinRM Kerberos complaint on a machine whose RPC never
             connected reads as an account problem when the fix is a
             firewall rule. The sentence and the fix now come from
             services/deploy_diagnosis.py; the raw log is in the drawer. */
          <div className="space-y-1">
            <div className="text-gray-500 dark:text-gray-400">Why it failed:</div>
            <div className="text-red-700 dark:text-red-300 leading-snug">{d.deploy_error}</div>
            {d.deploy_fix && (
              <>
                <div className="text-gray-500 dark:text-gray-400 pt-0.5">What fixes it:</div>
                <div className="text-gray-700 dark:text-gray-200 leading-snug">{d.deploy_fix}</div>
              </>
            )}
          </div>
        ) : (
          <div className="text-gray-500 dark:text-gray-400">
            {d.last_attempt_at
              ? `Last attempt: ${fmtWhen(d.last_attempt_at)}`
              : "No install attempt yet. Select the row and push the installer, or it auto-deploys on the next credentialed scan."}
          </div>
        )}
        <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-400">
          Click the row for full details
        </div>
      </div>
    </div>
  );
}

// Integration sync lifecycle → label + dot. Same visual language as the
// device StatusPill so Discovery and Integrations read consistently.
// Keyed on the DERIVED sync_health, not on last_sync_status.
// "success" plus a timestamp from three months ago is not health, and this
// pill was showing a green "Synced" against three integrations last heard
// from on 18 June. A status field answers "how did the last attempt go";
// it cannot answer "did an attempt happen", which is the question when a
// sync silently stops. See sync_health() in routers/network_integrations.py.
const SYNC_STATUS_META: Record<string, { label: string; dot: string; text: string }> = {
  ok: { label: "Synced", dot: "bg-green-500", text: "text-green-700 dark:text-green-300" },
  stale: { label: "Sync overdue", dot: "bg-amber-500", text: "text-amber-700 dark:text-amber-300" },
  failing: { label: "Sync failed", dot: "bg-red-500", text: "text-red-700 dark:text-red-300" },
  disabled: { label: "Disabled", dot: "bg-gray-300 dark:bg-gray-600", text: "text-gray-500 dark:text-gray-400" },
  never: { label: "Never synced", dot: "bg-gray-300 dark:bg-gray-600", text: "text-gray-500 dark:text-gray-400" },
  // Kept so an older cached payload still renders something sane.
  success: { label: "Synced", dot: "bg-green-500", text: "text-green-700 dark:text-green-300" },
  failure: { label: "Sync failed", dot: "bg-red-500", text: "text-red-700 dark:text-red-300" },
};
const syncMeta = (s?: string | null) => SYNC_STATUS_META[s || "never"] || SYNC_STATUS_META.never;

// Hover peek-card for an integration row: sync state + last error.
function SyncStatusPill({ i }: { i: Integration }) {
  const m = syncMeta(i.sync_health || i.last_sync_status);
  return (
    <div className="relative inline-flex group">
      <span className="inline-flex items-center gap-1.5 cursor-default">
        <span className={`w-2 h-2 rounded-full ${m.dot}`} />
        <span className={`text-xs font-medium ${m.text}`}>{m.label}</span>
        {!i.enabled && <span className="text-[10px] text-amber-600 dark:text-amber-400">(disabled)</span>}
      </span>
      <div
        className="pointer-events-none absolute left-0 top-full mt-1.5 z-30 hidden group-hover:block
          w-72 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900
          shadow-xl p-3 text-xs text-gray-700 dark:text-gray-200"
      >
        <div className="flex items-center gap-1.5 font-semibold mb-1">
          <span className={`w-2 h-2 rounded-full ${m.dot}`} />
          <span className={m.text}>{m.label}</span>
        </div>
        {i.last_sync_error ? (
          <div>
            <div className="text-gray-500 dark:text-gray-400 mb-0.5">Last sync error:</div>
            <div className="font-mono text-[11px] text-red-600 dark:text-red-400 whitespace-pre-wrap break-words max-h-32 overflow-hidden">
              {i.last_sync_error}
            </div>
          </div>
        ) : (
          <div className="space-y-0.5 text-gray-500 dark:text-gray-400">
            <div>Last sync: {fmtWhen(i.last_sync_at)}</div>
            <div>Devices imported: {i.last_device_count ?? "—"}</div>
          </div>
        )}
        <div className="mt-1.5 pt-1.5 border-t border-gray-100 dark:border-gray-800 text-[10px] text-gray-400">
          Click the row for full details
        </div>
      </div>
    </div>
  );
}

type SortKey =
  | "ip_address"
  | "hostname"
  | "vendor"
  | "model"
  | "device_class"
  | "open_ports"
  | "last_seen_at";

// IPv4 → sortable integer so 192.168.1.2 sorts before 192.168.1.10
// (a plain string sort would not). Non-IPv4 falls back to 0 (stable).
function ipToNum(ip: string): number {
  const parts = (ip || "").split(".");
  if (parts.length === 4) {
    return parts.reduce((acc, p) => acc * 256 + (parseInt(p, 10) || 0), 0);
  }
  return 0;
}

// Semantic (worst-first) rank maps for the status columns' click-to-sort.
const SCAN_STATUS_RANK: Record<string, number> = {
  failure: 0,
  running: 1,
  queued: 2,
  idle: 3,
  success: 4,
};
const SYNC_STATUS_RANK: Record<string, number> = { failure: 0, never: 1, success: 2 };

function ClassBadge({ cls }: { cls: string }) {
  const c = CLASS_COLOR[cls] || CLASS_COLOR.unknown;
  const l = CLASS_LABEL[cls] || cls;
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${c}`}>{l}</span>;
}

export default function NetworkDiscovery() {
  const { currentCustomerId } = useCustomer();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [devices, setDevices] = useState<Device[]>([]);
  const [customerId, setCustomerId] = useState<number | "">(currentCustomerId ?? "");
  const [targets, setTargets] = useState<ScanTarget[]>([]);
  const [discovered, setDiscovered] = useState<Discovered[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [integrations, setIntegrations] = useState<Integration[]>([]);
  const [err, setErr] = useState("");
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [showNewTarget, setShowNewTarget] = useState(false);
  const [showNewIntegration, setShowNewIntegration] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);
  const [deployCred, setDeployCred] = useState<DeployCred | null>(null);
  const [showCredForm, setShowCredForm] = useState(false);
  const [scanCidr, setScanCidr] = useState("192.168.1.0/24");
  // Click-through detail drawer.
  const [detail, setDetail] = useState<DiscoveredDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [integrationDetail, setIntegrationDetail] = useState<Integration | null>(null);

  const openDetail = async (id: number) => {
    setDetailLoading(true);
    setDetail(null);
    try {
      const r = await api.get(`/api/v1/network-discovery/discovered/${id}`);
      setDetail(r.data);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setDetailLoading(false);
    }
  };

  // ----- loaders -----
  const loadCustomers = async () => {
    const r = await api.get("/api/v1/customers");
    setCustomers((r.data || []).map((c: any) => ({ id: c.id, name: c.name })));
  };
  const loadDevices = async () => {
    const r = await api.get("/api/v1/devices");
    setDevices(
      (r.data || []).map((d: any) => ({
        id: d.id,
        hostname: d.hostname,
        customer_id: d.customer_id,
        status: d.status,
      }))
    );
  };
  const loadTargets = async () => {
    const params: any = {};
    if (customerId !== "") params.customer_id = customerId;
    const r = await api.get("/api/v1/network-discovery/targets", { params });
    setTargets(r.data || []);
  };
  const loadDiscovered = async () => {
    const params: any = { include_ignored: false, include_managed: false };
    if (customerId !== "") params.customer_id = customerId;
    const r = await api.get("/api/v1/network-discovery/discovered", { params });
    setDiscovered(r.data || []);
    setSelectedIds(new Set());
  };
  const loadVendors = async () => {
    const r = await api.get("/api/v1/network-integrations/vendors");
    setVendors(r.data?.vendors || []);
  };
  const loadIntegrations = async () => {
    const params: any = {};
    if (customerId !== "") params.customer_id = customerId;
    const r = await api.get("/api/v1/network-integrations", { params });
    setIntegrations(r.data || []);
  };
  const loadDeployCred = async () => {
    if (customerId === "") {
      setDeployCred(null);
      return;
    }
    const r = await api.get("/api/v1/network-discovery/deploy-credentials", {
      params: { customer_id: customerId },
    });
    setDeployCred(r.data || null);
  };

  useEffect(() => {
    loadCustomers().catch(() => {});
    loadDevices().catch(() => {});
    loadVendors().catch(() => {});
  }, []);
  // The global customer switcher drives this page's scope.
  useEffect(() => {
    setCustomerId(currentCustomerId ?? "");
  }, [currentCustomerId]);
  useEffect(() => {
    loadTargets().catch((e) => setErr(e.response?.data?.detail || e.message));
    loadDiscovered().catch((e) => setErr(e.response?.data?.detail || e.message));
    loadIntegrations().catch((e) => setErr(e.response?.data?.detail || e.message));
    loadDeployCred().catch((e) => setErr(e.response?.data?.detail || e.message));
    // Re-fetch the customer-scoped lists whenever the customer filter changes;
    // the loaders close over customerId, so it is the only meaningful trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerId]);

  // ----- actions -----
  const triggerScan = async (id: number) => {
    setErr("");
    try {
      await api.post(`/api/v1/network-discovery/targets/${id}/trigger`);
      await loadTargets();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
    }
  };
  const deleteTarget = async (id: number) => {
    if (!window.confirm("Delete this scan target?")) return;
    try {
      await api.delete(`/api/v1/network-discovery/targets/${id}`);
      await loadTargets();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  const ignoreOne = async (id: number) => {
    const reason = window.prompt("Reason for ignoring (optional):") || "";
    try {
      await api.post(`/api/v1/network-discovery/discovered/${id}/ignore`, { reason });
      await loadDiscovered();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  const promoteSnmp = async (d: Discovered) => {
    const name = window.prompt(
      "Display name for this network device:",
      d.hostname || d.snmp_info?.sys_name || d.ip_address
    );
    if (!name) return;
    const community = window.prompt("SNMP community (default 'public'):", "public") || "public";
    try {
      const kindMap: Record<string, string> = {
        switch: "switch",
        router: "router",
        firewall: "firewall",
        access_point: "access_point",
        printer: "printer",
      };
      await api.post(`/api/v1/network-discovery/discovered/${d.id}/promote-snmp`, {
        name,
        kind: kindMap[d.device_class] || "other",
        snmp_version: "v2c",
        snmp_community: community,
        snmp_port: 161,
      });
      await loadDiscovered();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  // Monitor any device as a ping-only (ICMP) network device — for gear that
  // isn't SNMP-capable. Creates a NetworkDevice (up/down + alert on down).
  const monitorIcmp = async (d: Discovered) => {
    const name = window.prompt(
      "Display name for this network device (ICMP / ping-only):",
      d.hostname || d.ip_address
    );
    if (!name) return;
    const kindMap: Record<string, string> = {
      printer: "printer",
      switch: "switch",
      router: "router",
      firewall: "firewall",
      access_point: "access_point",
      nas: "nas",
      ups: "ups",
    };
    const defaultKind = kindMap[d.device_class] || "other";
    const kind =
      window.prompt(
        "Device type (switch, router, firewall, printer, ups, access_point, nas, other):",
        defaultKind
      ) || defaultKind;
    try {
      await api.post(`/api/v1/network-discovery/discovered/${d.id}/promote-icmp`, {
        name,
        kind: kind.trim().toLowerCase(),
      });
      await loadDiscovered();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  // Manually (re)classify a discovered device the RMM couldn't identify.
  const classifyDevice = async (id: number, cls: string) => {
    try {
      await api.post(`/api/v1/network-discovery/discovered/${id}/classify`, {
        device_class: cls,
      });
      await loadDiscovered();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  const pushInstallerSelected = async () => {
    if (selectedIds.size === 0) {
      window.alert("Select at least one discovered device first.");
      return;
    }
    if (!window.confirm(`Push the agent installer to ${selectedIds.size} discovered device(s)?`)) return;
    try {
      const r = await api.post("/api/v1/network-discovery/discovered/push-installer", {
        discovered_device_ids: Array.from(selectedIds),
      });
      window.alert(
        `Queued install ${
          r.data.credentialed
            ? "via an on-site agent using the saved deploy credentials"
            : "via the customer's AD-runner agent (its own service account)"
        }. Command #${r.data.command_id}, ${r.data.target_count} target(s).`
      );
      await loadDiscovered();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
    }
  };
  const scanAndDeploy = async () => {
    if (customerId === "") return;
    if (!deployCred?.configured) {
      window.alert("Set deploy credentials for this customer first.");
      return;
    }
    if (
      !window.confirm(
        `Scan ${scanCidr} from an on-site agent and AUTOMATICALLY install the agent on every unmanaged Windows device found, using the saved deploy credentials?`
      )
    )
      return;
    setErr("");
    try {
      const r = await api.post("/api/v1/network-discovery/scan-and-deploy", {
        customer_id: customerId,
        cidr: scanCidr,
      });
      window.alert(
        `Scan queued on ${
          r.data.scanner_hostname || "the on-site agent"
        } for ${r.data.cidr}. The agent auto-installs on discovered Windows devices once results come back — watch the Discovered list + Audit log.`
      );
      await loadTargets();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
    }
  };
  const clearCred = async () => {
    if (customerId === "") return;
    if (!window.confirm("Remove the saved deploy credential for this customer?")) return;
    try {
      await api.delete(`/api/v1/network-discovery/deploy-credentials/${customerId}`);
      await loadDeployCred();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };
  // One-click Group Policy deploy: download a script the Domain Admin runs once
  // on a DC to push the agent to every domain computer (no WinRM, no per-device).
  const downloadGpoScript = async () => {
    if (customerId === "") return;
    try {
      const r = await api.get("/api/v1/onboarding/gpo-script", {
        params: { customer_id: customerId, format: "cmd" },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([r.data], { type: "application/octet-stream" })
      );
      const a = document.createElement("a");
      a.href = url;
      a.download = "AeroGauge-GPO-Deploy.cmd";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  // ----- integrations actions -----
  const probeIntegration = async (id: number) => {
    try {
      const r = await api.post(`/api/v1/network-integrations/${id}/probe`);
      window.alert((r.data.ok ? "OK: " : "FAIL: ") + r.data.message);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
    }
  };
  const syncIntegration = async (id: number) => {
    try {
      const r = await api.post(`/api/v1/network-integrations/${id}/sync`);
      if (r.data.status === "success") {
        window.alert(`Synced ${r.data.device_count} device(s).`);
      } else if (r.data.skipped) {
        // Relay is on. Not running here is the CORRECT outcome, so reporting it
        // as "Sync failed" would be a lie — and it was previously printing
        // "Sync failed: null", which is worse than saying nothing.
        window.alert(
          r.data.message ||
            "This integration syncs from the customer's network. Their agent will run it shortly.",
        );
      } else {
        window.alert(`Sync failed: ${r.data.error || "no reason reported"}`);
      }
      await Promise.all([loadIntegrations(), loadDiscovered()]);
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
    }
  };
  const deleteIntegration = async (id: number) => {
    if (!window.confirm("Delete this integration?")) return;
    try {
      await api.delete(`/api/v1/network-integrations/${id}`);
      await loadIntegrations();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  const customerOptions = useMemo(() => customers.map((c) => ({ value: c.id, label: c.name })), [customers]);
  const customerDevices = useMemo(
    () =>
      customerId === ""
        ? []
        : devices.filter((d) => d.customer_id === Number(customerId)),
    [devices, customerId]
  );

  // ----- sorting + select-all for the Discovered table -----
  const [sortKey, setSortKey] = useState<SortKey>("ip_address");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  // Independent sort state for the Scan targets + Vendor integrations tables.
  const targetSort = useSort("cidr", "asc");

  // Publish the visible rows so the global AI Assist can answer
  // "what's on my screen". Projected explicitly: this payload goes
  // into a model prompt.
  usePublishScreenData(
    "Network discovery",
    useMemo(
      () =>
        discovered.map((d: any) => ({
          hostname: d.hostname ?? null,
          ip: d.ip_address ?? null,
          mac: d.mac_address ?? null,
          vendor: d.vendor ?? null,
          has_agent: d.has_agent ?? null,
          deploy_status: d.deploy_status ?? null,
          last_seen: d.last_seen ?? null,
          // NOT the deploy LOG - it is verbatim installer output and can
          // contain a credential the deploy used.
        })),
      [discovered],
    ),
    `${targets.length} scan target(s) configured`,
  );
  const intSort = useSort("vendor", "asc");
  const sortedDiscovered = useMemo(() => {
    const val = (d: Discovered): string | number => {
      switch (sortKey) {
        case "ip_address":
          return ipToNum(d.ip_address);
        case "hostname":
          return (d.hostname || "").toLowerCase();
        case "vendor":
          return (d.vendor || d.mac_address || "").toLowerCase();
        case "model":
          return String(d.snmp_info?.model || "").toLowerCase();
        case "device_class":
          return d.device_class || "";
        case "open_ports":
          return d.open_ports && d.open_ports.length ? Math.min(...d.open_ports) : 1e9;
        case "last_seen_at":
          return utcMillis(d.last_seen_at);
        default:
          return "";
      }
    };
    return [...discovered].sort((a, b) => {
      const va = val(a);
      const vb = val(b);
      const c = va < vb ? -1 : va > vb ? 1 : 0;
      return sortDir === "asc" ? c : -c;
    });
  }, [discovered, sortKey, sortDir]);
  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    else {
      setSortKey(key);
      setSortDir("asc");
    }
  };
  const installableRows = discovered.filter(isInstallable);
  const allInstallableSelected =
    installableRows.length > 0 && installableRows.every((d) => selectedIds.has(d.id));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">Network Discovery</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
          Sweep customer subnets for unmanaged endpoints, pull device inventories from network vendor APIs, and push the
          agent installer to anything you want to manage. Works the same whether the RMM server is on-prem or off-prem —
          agents call out to whichever URL you configured.
        </p>
      </div>

      {err && (
        <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 px-4 py-2 rounded-lg text-sm">{err}</div>
      )}

      <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
        <span>Customer:</span>
        <span className="font-medium text-gray-800 dark:text-gray-100">
          {customerId === ""
            ? "All customers"
            : customers.find((c) => c.id === customerId)?.name || `#${customerId}`}
        </span>
        {customerId === "" && (
          <span className="text-xs text-gray-400">
            — pick one in the header switcher to add scan targets
          </span>
        )}
      </div>

      {/* Credentialed deployment ------------------------------------ */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700">
          <h2 className="font-medium text-gray-800 dark:text-gray-100">Credentialed deployment</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Save an admin login once per customer, then push the agent to discovered Windows
            devices in one click — or scan a subnet and auto-install on everything found. The
            password is encrypted at rest and only ever handed to the on-site agent at install time.
          </p>
        </div>
        <div className="p-4">
          {customerId === "" ? (
            <div className="text-sm text-gray-500 dark:text-gray-400">
              Pick a customer in the header switcher to manage deploy credentials.
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div className="text-sm">
                  {deployCred?.configured ? (
                    <span className="text-gray-700 dark:text-gray-200">
                      Deploy credential saved:{" "}
                      <span className="font-mono">
                        {deployCred.domain ? `${deployCred.domain}\\` : ""}
                        {deployCred.username}
                      </span>
                    </span>
                  ) : (
                    <span className="text-amber-600 dark:text-amber-400">
                      No deploy credential saved — set one to enable one-click + auto deploy.
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowCredForm(true)}
                    className="px-3 py-1.5 border border-brand text-brand dark:text-brand-light rounded-lg text-sm hover:bg-brand/10"
                  >
                    {deployCred?.configured ? "Update credentials" : "Set credentials"}
                  </button>
                  {deployCred?.configured && (
                    <ActionButton onAction={() => clearCred()} variant="raw" className="px-3 py-1.5 border border-red-200 text-red-700 dark:border-red-800 dark:text-red-300 rounded-lg text-sm hover:bg-red-50 dark:hover:bg-red-900/30" pendingLabel="Clearing…">
                      Remove
                    </ActionButton>
                  )}
                </div>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
                  One-click scan &amp; auto-deploy
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-snug">
                  Scans the subnet from an on-site agent, then auto-installs the agent on every
                  unmanaged Windows host it finds. Requires an online agent on that LAN and saved
                  credentials. Targets must have WinRM (PSRemoting) reachable, and the login needs
                  local admin on them.
                </p>
                <div className="flex gap-2 items-center flex-wrap">
                  <input
                    value={scanCidr}
                    onChange={(e) => setScanCidr(e.target.value)}
                    placeholder="192.168.1.0/24"
                    className="border rounded px-3 py-2 text-sm font-mono dark:bg-gray-900 dark:border-gray-600"
                  />
                  <ActionButton
                    onAction={scanAndDeploy}
                    pendingLabel="Starting…"
                    size="sm"
                    disabled={!deployCred?.configured}
                    className="bg-brand text-white hover:opacity-90 rounded-lg border-transparent"
                    variant="ghost"
                    title={
                      !deployCred?.configured
                        ? "Set deploy credentials first"
                        : "Sweep the subnet and install on everything found"
                    }
                  >
                    Scan &amp; auto-deploy
                  </ActionButton>
                </div>
              </div>
              <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
                <div className="text-sm font-medium text-gray-800 dark:text-gray-100 mb-1">
                  Deploy to a whole Windows domain (Group Policy)
                </div>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-2 leading-snug">
                  Downloads a script the customer's Domain Admin runs once on a Domain Controller.
                  It creates a Group Policy that installs the agent on every domain computer at next
                  reboot — no per-device push, no WinRM. Best option for domain-joined fleets.
                </p>
                <ActionButton onAction={() => downloadGpoScript()} variant="raw" className="px-3 py-1.5 bg-brand text-white rounded-lg text-sm hover:opacity-90" pendingLabel="Preparing…">
                  Deploy via Group Policy
                </ActionButton>
              </div>
            </div>
          )}
        </div>
      </div>

      {showCredForm && customerId !== "" && (
        <DeployCredForm
          customerId={Number(customerId)}
          existing={deployCred}
          onClose={() => setShowCredForm(false)}
          onSaved={async () => {
            setShowCredForm(false);
            await loadDeployCred();
          }}
        />
      )}

      {/* Scan targets ------------------------------------------------ */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-800 dark:text-gray-100">Scan targets</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              CIDRs the agent on the customer's LAN will sweep on its schedule.
            </p>
          </div>
          <button
            onClick={() => setShowNewTarget(true)}
            disabled={customerId === ""}
            className="px-3 py-1.5 bg-brand text-white rounded-lg text-sm hover:opacity-90 disabled:opacity-50"
            title={customerId === "" ? "Pick a customer first" : ""}
          >
            + Add subnet
          </button>
        </div>
        {targets.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            No scan targets yet. Pick a customer and add one to begin.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500">
              <tr>
                <SortableTh label="CIDR" sortKey="cidr" sort={targetSort.sort} onSort={targetSort.toggle} />
                <SortableTh label="Customer" sortKey="customer_name" sort={targetSort.sort} onSort={targetSort.toggle} hide="hidden md:table-cell" />
                <SortableTh label="Scanner" sortKey="scanner_hostname" sort={targetSort.sort} onSort={targetSort.toggle} hide="hidden lg:table-cell" />
                <SortableTh label="Schedule" sortKey="scan_interval_minutes" sort={targetSort.sort} onSort={targetSort.toggle} hide="hidden xl:table-cell" />
                <SortableTh label="Last scan" sortKey="last_scan_status" sort={targetSort.sort} onSort={targetSort.toggle} hide="hidden sm:table-cell" />
                <SortableTh label="Discovered" sortKey="last_discovered_count" sort={targetSort.sort} onSort={targetSort.toggle} />
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(targets, targetSort.sort, (t, key) => {
                switch (key) {
                  case "customer_name":
                    return (t.customer_name || `#${t.customer_id}`).toLowerCase();
                  case "scanner_hostname":
                    return (t.scanner_hostname || "").toLowerCase();
                  case "scan_interval_minutes":
                    return t.scan_interval_minutes;
                  case "last_scan_status":
                    return SCAN_STATUS_RANK[t.last_scan_status || "idle"] ?? 99;
                  case "last_discovered_count":
                    return t.last_discovered_count ?? -1;
                  default:
                    return (t.cidr || "").toLowerCase();
                }
              }).map((t) => (
                <tr key={t.id}>
                  <td className="px-3 py-2 font-mono">
                    {/* CIDR plus its optional label came to 217px of a 366px
                        container. The label wraps under the CIDR on a phone
                        instead of extending the row sideways. */}
                    <div className="truncate max-w-[9rem] sm:max-w-none">{t.cidr}</div>
                    {t.label ? (
                      <div className="text-gray-400 text-xs sm:inline sm:ml-2 truncate max-w-[9rem] sm:max-w-none">
                        ({t.label})
                      </div>
                    ) : null}
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">{t.customer_name || `#${t.customer_id}`}</td>
                  <td className="px-3 py-2 hidden lg:table-cell">{t.scanner_hostname || <span className="text-gray-400">auto</span>}</td>
                  <td className="px-3 py-2 hidden xl:table-cell">{Math.round(t.scan_interval_minutes / 60 * 10) / 10}h</td>
                  <td className="px-3 py-2 text-xs hidden sm:table-cell">
                    {t.last_scan_status || "idle"}
                    {t.last_scan_completed_at && ` · ${formatDateTime(t.last_scan_completed_at)}`}
                    {t.last_scan_error && <div className="text-red-600 dark:text-red-400">{t.last_scan_error}</div>}
                  </td>
                  <td className="px-3 py-2">{t.last_discovered_count ?? "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <ActionButton
                      onAction={() => triggerScan(t.id)}
                      pendingLabel="Queuing…"
                      className="mr-2"
                      title="Queue a sweep of this subnet on the customer's agent"
                    >
                      Scan now
                    </ActionButton>
                    <button
                      onClick={() => deleteTarget(t.id)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-700 dark:border-red-800 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNewTarget && customerId !== "" && (
        <NewTargetForm
          customerId={Number(customerId)}
          customerDevices={customerDevices}
          onClose={() => setShowNewTarget(false)}
          onCreated={async () => {
            setShowNewTarget(false);
            await loadTargets();
          }}
        />
      )}

      {/* Vendor integrations ---------------------------------------- */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-800 dark:text-gray-100">Vendor API integrations</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Cisco Meraki, Ubiquiti UniFi, FortiGate, SonicWall, Aruba Central, PAN-OS, MikroTik, Sophos Central,
              WatchGuard, UniFi Protect. Imports inventory into the discovery list.
            </p>
          </div>
          <button
            onClick={() => setShowNewIntegration(true)}
            className="px-3 py-1.5 bg-brand text-white rounded-lg text-sm hover:opacity-90"
          >
            + Add integration
          </button>
        </div>
        {integrations.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">No integrations yet.</div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500">
              <tr>
                <SortableTh label="Vendor" sortKey="vendor" sort={intSort.sort} onSort={intSort.toggle} />
                <SortableTh label="Name" sortKey="name" sort={intSort.sort} onSort={intSort.toggle} />
                <SortableTh label="Customer" sortKey="customer_name" sort={intSort.sort} onSort={intSort.toggle} />
                <SortableTh label="Status" sortKey="last_sync_status" sort={intSort.sort} onSort={intSort.toggle} />
                <SortableTh label="Last sync" sortKey="last_sync_at" sort={intSort.sort} onSort={intSort.toggle} />
                <SortableTh label="Devices" sortKey="last_device_count" sort={intSort.sort} onSort={intSort.toggle} />
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortRows(integrations, intSort.sort, (i, key) => {
                switch (key) {
                  case "name":
                    return (i.name || "").toLowerCase();
                  case "customer_name":
                    return (i.customer_name || `#${i.customer_id}`).toLowerCase();
                  case "last_sync_status":
                    return SYNC_STATUS_RANK[i.last_sync_status || "never"] ?? 99;
                  case "last_sync_at":
                    return i.last_sync_at ? Date.parse(i.last_sync_at) : 0;
                  case "last_device_count":
                    return i.last_device_count ?? -1;
                  default:
                    return (i.vendor || "").toLowerCase();
                }
              }).map((i) => (
                <tr
                  key={i.id}
                  onClick={() => setIntegrationDetail(i)}
                  className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                  title="Click for full integration detail + sync error"
                >
                  <td className="px-3 py-2 font-medium">{vendors.find((v) => v.vendor === i.vendor)?.label || i.vendor}</td>
                  <td className="px-3 py-2">{i.name}</td>
                  <td className="px-3 py-2">{i.customer_name || `#${i.customer_id}`}</td>
                  <td className="px-3 py-2"><SyncStatusPill i={i} /></td>
                  <td className="px-3 py-2 text-xs">
                    {i.last_sync_at ? formatDateTime(i.last_sync_at) : "—"}
                  </td>
                  <td className="px-3 py-2">{i.last_device_count ?? "—"}</td>
                  <td className="px-3 py-2 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                    <ActionButton
                      onAction={() => probeIntegration(i.id)}
                      pendingLabel="Testing…"
                      title="Check the credential and reach the controller"
                    >
                      Test
                    </ActionButton>
                    <ActionButton
                      onAction={() => syncIntegration(i.id)}
                      pendingLabel="Syncing…"
                      disabled={!i.enabled}
                      title={i.enabled ? "Pull inventory now" : "Integration is disabled"}
                    >
                      Sync now
                    </ActionButton>
                    <button
                      onClick={() => setEditingIntegration(i)}
                      className="text-xs px-2 py-1 border border-brand text-brand dark:text-brand-light rounded hover:bg-brand/10"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteIntegration(i.id)}
                      className="text-xs px-2 py-1 border border-red-200 text-red-700 dark:border-red-800 dark:text-red-300 rounded hover:bg-red-50 dark:hover:bg-red-900/30"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showNewIntegration && (
        <NewIntegrationForm
          customerId={customerId === "" ? null : Number(customerId)}
          customerOptions={customerOptions}
          vendors={vendors}
          onClose={() => setShowNewIntegration(false)}
          onCreated={async () => {
            setShowNewIntegration(false);
            await loadIntegrations();
          }}
        />
      )}

      {editingIntegration && (
        <EditIntegrationForm
          integration={editingIntegration}
          vendors={vendors}
          onClose={() => setEditingIntegration(null)}
          onSaved={async () => {
            setEditingIntegration(null);
            await loadIntegrations();
          }}
        />
      )}

      {/* Discovered devices ----------------------------------------- */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-100 dark:border-gray-700 shadow-sm">
        <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
          <div>
            <h2 className="font-medium text-gray-800 dark:text-gray-100">Discovered devices ({discovered.length})</h2>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Hits from agent scans + vendor API imports. Select rows to push the agent installer.
            </p>
          </div>
          <div className="flex gap-2">
            <ActionButton
              onAction={pushInstallerSelected}
              pendingLabel="Pushing…"
              size="sm"
              disabled={selectedIds.size === 0}
              className="bg-brand text-white hover:opacity-90 rounded-lg border-transparent"
              variant="ghost"
              title={
                selectedIds.size === 0
                  ? "Select one or more discovered devices first"
                  : "Install the agent on the selected devices"
              }
            >
              Push installer ({selectedIds.size})
            </ActionButton>
            <ActionButton onAction={() => loadDiscovered()} variant="raw" className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm" pendingLabel="Adding…">
              Refresh
            </ActionButton>
          </div>
        </div>
        {discovered.length === 0 ? (
          <div className="p-4 text-sm text-gray-500 dark:text-gray-400">
            Nothing discovered yet. Trigger a scan or sync an integration above.
          </div>
        ) : (
          <table className="min-w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-900 text-xs uppercase text-gray-500">
              <tr>
                <th className="px-3 py-2 w-8">
                  <input
                    type="checkbox"
                    aria-label="Select all installable devices"
                    title="Select all installable (Windows/Linux) devices"
                    checked={allInstallableSelected}
                    onChange={(e) =>
                      setSelectedIds(
                        e.target.checked ? new Set(installableRows.map((d) => d.id)) : new Set()
                      )
                    }
                  />
                </th>
                <th className="px-3 py-2 text-left">Status</th>
                {(
                  [
                    // Third element = the responsive hide class. It lives in
                    // the tuple because these headers are generated; a class
                    // written on the <th> below would apply to all seven.
                    // Each MUST match the matching <td> exactly.
                    ["IP", "ip_address", ""],
                    ["Hostname", "hostname", "hidden sm:table-cell"],
                    ["MAC / Vendor", "vendor", "hidden lg:table-cell"],
                    ["Model", "model", "hidden lg:table-cell"],
                    ["Class", "device_class", "hidden md:table-cell"],
                    ["Open ports", "open_ports", "hidden xl:table-cell"],
                    ["Seen", "last_seen_at", "hidden md:table-cell"],
                  ] as [string, SortKey, string][]
                ).map(([label, key, hide]) => (
                  <th
                    key={key}
                    onClick={() => toggleSort(key)}
                    className={`px-3 py-2 text-left cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-300 ${hide}`}
                  >
                    {label}
                    {sortKey === key ? (sortDir === "asc" ? " ▲" : " ▼") : ""}
                  </th>
                ))}
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {sortedDiscovered.map((d) => {
                const installable = isInstallable(d);
                return (
                  <tr
                    key={d.id}
                    onClick={() => openDetail(d.id)}
                    className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/40"
                    title="Click for full discovered info + deploy log"
                  >
                    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedIds.has(d.id)}
                        disabled={!installable}
                        onChange={(e) => {
                          const s = new Set(selectedIds);
                          if (e.target.checked) s.add(d.id); else s.delete(d.id);
                          setSelectedIds(s);
                        }}
                      />
                    </td>
                    <td className="px-3 py-2"><StatusPill d={d} /></td>
                    <td className="px-3 py-2 font-mono">
                      {d.ip_address}
                      {/* Hostname is hidden below sm; show it here so a phone
                          row still identifies the box, not just its address. */}
                      {d.hostname ? (
                        <div className="sm:hidden font-sans text-[11px] text-gray-500 dark:text-gray-400 truncate max-w-[9rem]">
                          {d.hostname}
                        </div>
                      ) : null}
                    </td>
                    <td className="px-3 py-2 hidden sm:table-cell">{d.hostname || <span className="text-gray-400">—</span>}</td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">
                      <div className="font-mono">{d.mac_address || "—"}</div>
                      <div className="text-gray-500">{d.vendor || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-xs hidden lg:table-cell">
                      {d.snmp_info?.model ? (
                        <>
                          <div className="font-mono font-medium text-gray-900 dark:text-gray-100">
                            {d.snmp_info.model}
                          </div>
                          {modelCategoryLabel(d.snmp_info.model) && (
                            <div className="text-[10px] text-gray-500">
                              {modelCategoryLabel(d.snmp_info.model)}
                            </div>
                          )}
                          {d.snmp_info?.serial && (
                            <div className="text-[10px] text-gray-400 font-mono">
                              SN: {d.snmp_info.serial}
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                      <ClassBadge cls={d.device_class} />
                      <select
                        value=""
                        onChange={(e) => {
                          if (e.target.value) classifyDevice(d.id, e.target.value);
                        }}
                        title="Manually set this device's classification"
                        className="block mt-1 text-[10px] border border-gray-200 dark:border-gray-600 rounded bg-transparent text-gray-500 max-w-[8.5rem]"
                      >
                        <option value="">Set class…</option>
                        {MANUAL_CLASSES.map((c) => (
                          <option key={c} value={c}>
                            {CLASS_LABEL[c] || c}
                          </option>
                        ))}
                      </select>
                      {d.class_overridden && (
                        <span className="block text-[9px] text-gray-400">manually set</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-xs hidden xl:table-cell">{(d.open_ports || []).join(", ") || "—"}</td>
                    <td className="px-3 py-2 text-xs hidden md:table-cell">
                      {d.seen_count}× · {formatDateTime(d.last_seen_at)}
                    </td>
                    <td className="px-3 py-2 text-right space-x-2" onClick={(e) => e.stopPropagation()}>
                      {!d.promoted_network_device_id && !d.managed_device_id && (
                        <>
                          <button
                            onClick={() => promoteSnmp(d)}
                            title="Full SNMP monitoring — polls the device for status, model, ports/VLANs (device must be SNMP-capable)"
                            className="text-xs px-2 py-1 border border-purple-300 text-purple-700 dark:text-purple-300 dark:border-purple-700 rounded hover:bg-purple-50 dark:hover:bg-purple-900/30"
                          >
                            Monitor (SNMP)
                          </button>
                          <button
                            onClick={() => monitorIcmp(d)}
                            title="Ping-only up/down monitoring — for devices that aren't SNMP-capable; alerts when it goes down"
                            className="text-xs px-2 py-1 border border-teal-300 text-teal-700 dark:text-teal-300 dark:border-teal-700 rounded hover:bg-teal-50 dark:hover:bg-teal-900/30"
                          >
                            Monitor (ICMP)
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => ignoreOne(d.id)}
                        className="text-xs px-2 py-1 border border-gray-300 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700"
                      >
                        Ignore
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {(detail || detailLoading) && (
        <DetailDrawer
          detail={detail}
          loading={detailLoading}
          onClose={() => setDetail(null)}
        />
      )}

      {integrationDetail && (
        <IntegrationDrawer
          i={integrationDetail}
          vendorLabel={
            vendors.find((v) => v.vendor === integrationDetail.vendor)?.label ||
            integrationDetail.vendor
          }
          onClose={() => setIntegrationDetail(null)}
        />
      )}
    </div>
  );
}

// ---------- Detail drawer (right slide-over) ----------

function DrawerRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 py-1.5 border-b border-gray-100 dark:border-gray-800 last:border-0">
      <div className="w-32 shrink-0 text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div className="flex-1 text-sm text-gray-800 dark:text-gray-100 break-words">{children}</div>
    </div>
  );
}

function DetailDrawer({
  detail,
  loading,
  onClose,
}: {
  detail: DiscoveredDetail | null;
  loading: boolean;
  onClose: () => void;
}) {
  const m = detail ? deployMeta(detail.deploy_status) : deployMeta("never");
  const log = detail?.deploy_stderr || detail?.deploy_stdout || "";
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" {...backdropClose(onClose)} />
      <div
        className="fixed inset-y-0 right-0 w-[30rem] max-w-[94vw] z-50 overflow-y-auto
          bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">
              {detail?.hostname || detail?.ip_address || "Device"}
            </h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-2 h-2 rounded-full ${m.dot}`} />
              <span className={`text-xs font-medium ${m.text}`}>{m.label}</span>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>

        {loading || !detail ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : (
          <div className="p-5 space-y-5">
            <section>
              <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Identity</h4>
              <DrawerRow label="IP address"><span className="font-mono">{detail.ip_address}</span></DrawerRow>
              <DrawerRow label="Hostname">{detail.hostname || "—"}</DrawerRow>
              <DrawerRow label="MAC"><span className="font-mono">{detail.mac_address || "—"}</span></DrawerRow>
              <DrawerRow label="Vendor">{detail.vendor || "—"}</DrawerRow>
              <DrawerRow label="OS guess">{detail.os_guess || "—"}</DrawerRow>
              <DrawerRow label="Class"><ClassBadge cls={detail.device_class} />{detail.class_overridden ? <span className="ml-1 text-[10px] text-gray-400">(manual)</span> : null}</DrawerRow>
              <DrawerRow label="Open ports">{(detail.open_ports || []).join(", ") || "—"}</DrawerRow>
            </section>

            <section>
              <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Discovery</h4>
              <DrawerRow label="First seen">{fmtWhen(detail.first_seen_at)}</DrawerRow>
              <DrawerRow label="Last seen">{fmtWhen(detail.last_seen_at)}</DrawerRow>
              <DrawerRow label="Times seen">{detail.seen_count}×</DrawerRow>
            </section>

            {detail.deploy_status === "installed" && (
              <section>
                <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Agent</h4>
                <DrawerRow label="Agent host">{detail.agent_hostname || "—"}</DrawerRow>
                <DrawerRow label="Status">
                  <span className={detail.agent_status === "online" ? "text-green-600 dark:text-green-400" : "text-red-600 dark:text-red-400"}>
                    {detail.agent_status || "unknown"}
                  </span>
                </DrawerRow>
                <DrawerRow label="Version">{detail.agent_version || "—"}</DrawerRow>
                <DrawerRow label="Last check-in">{fmtWhen(detail.agent_last_seen)}</DrawerRow>
              </section>
            )}

            {detail.last_attempt_at && (
              <section>
                <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Last deploy attempt</h4>
                <DrawerRow label="When">{fmtWhen(detail.last_attempt_at)}</DrawerRow>
                <DrawerRow label="By">{detail.deploy_attempted_by || "—"}</DrawerRow>
                <DrawerRow label="Credentialed">{detail.deploy_credentialed ? "Yes (saved creds)" : "No (AD runner)"}</DrawerRow>
                <DrawerRow label="Command">
                  {detail.deploy_command_status || "—"}
                  {detail.deploy_exit_code != null ? ` (exit ${detail.deploy_exit_code})` : ""}
                </DrawerRow>
                {/* The diagnosis goes ABOVE the log, and the log stays.
                    A technician needs the sentence to act on; the verbatim
                    output is what they send upstream when the sentence is
                    not enough, and removing it would lose the only record
                    of what the three transports actually said. */}
                {detail.deploy_error && detail.deploy_status === "failed" && (
                  <div className="mt-2 rounded-lg border border-red-200 dark:border-red-900 bg-red-50 dark:bg-red-900/20 p-3">
                    <div className="text-xs font-semibold text-red-800 dark:text-red-300 mb-1">
                      Why it failed
                    </div>
                    <p className="text-xs text-red-800 dark:text-red-200 leading-snug">
                      {detail.deploy_error}
                    </p>
                    {detail.deploy_fix && (
                      <>
                        <div className="text-xs font-semibold text-gray-700 dark:text-gray-200 mt-2 mb-1">
                          What fixes it
                        </div>
                        <p className="text-xs text-gray-700 dark:text-gray-300 leading-snug">
                          {detail.deploy_fix}
                        </p>
                      </>
                    )}
                  </div>
                )}
                {log && (
                  <div className="mt-2">
                    <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Deploy output</div>
                    <pre className="text-[11px] font-mono whitespace-pre-wrap break-words rounded-lg p-3 max-h-72 overflow-auto
                      bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200">
                      {log}
                    </pre>
                  </div>
                )}
              </section>
            )}

            {detail.ignored_at && (
              <section>
                <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Ignored</h4>
                <DrawerRow label="When">{fmtWhen(detail.ignored_at)}</DrawerRow>
                <DrawerRow label="Reason">{detail.ignore_reason || "—"}</DrawerRow>
              </section>
            )}

            {detail.snmp_info && Object.keys(detail.snmp_info).length > 0 && (
              <section>
                <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">SNMP</h4>
                <pre className="text-[11px] font-mono whitespace-pre-wrap break-words rounded-lg p-3 max-h-60 overflow-auto
                  bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200">
                  {JSON.stringify(detail.snmp_info, null, 2)}
                </pre>
              </section>
            )}
          </div>
        )}
      </div>
    </>
  );
}

// ---------- Integration detail drawer ----------

function IntegrationDrawer({
  i,
  vendorLabel,
  onClose,
}: {
  i: Integration;
  vendorLabel: string;
  onClose: () => void;
}) {
  const m = syncMeta(i.last_sync_status);
  const hasConfig = i.extra_config && Object.keys(i.extra_config).length > 0;
  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" {...backdropClose(onClose)} />
      <div
        className="fixed inset-y-0 right-0 w-[30rem] max-w-[94vw] z-50 overflow-y-auto
          bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-700"
      >
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-5 py-3 flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">{i.name}</h3>
            <div className="flex items-center gap-1.5 mt-0.5">
              <span className={`w-2 h-2 rounded-full ${m.dot}`} />
              <span className={`text-xs font-medium ${m.text}`}>{m.label}</span>
              {!i.enabled && <span className="text-[10px] text-amber-600 dark:text-amber-400">· disabled</span>}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 text-xl leading-none"
            aria-label="Close"
          >
            ×
          </button>
        </div>
        <div className="p-5 space-y-5">
          <section>
            <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Connection</h4>
            <DrawerRow label="Vendor">{vendorLabel}</DrawerRow>
            <DrawerRow label="Customer">{i.customer_name || `#${i.customer_id}`}</DrawerRow>
            <DrawerRow label="API base URL">{i.api_base_url || <span className="text-gray-400">default</span>}</DrawerRow>
            <DrawerRow label="API key">{i.has_api_key ? "Set" : <span className="text-amber-600 dark:text-amber-400">Not set</span>}</DrawerRow>
            <DrawerRow label="Enabled">{i.enabled ? "Yes" : "No"}</DrawerRow>
            <DrawerRow label="Sync interval">{i.sync_interval_minutes} min</DrawerRow>
            <DrawerRow label="Sync runs from">
              {i.poll_via_agent ? (
                <span>
                  An agent on the customer's network
                  {i.collector_device_id ? " (pinned)" : " (any online)"}
                </span>
              ) : (
                "The AeroGauge appliance"
              )}
            </DrawerRow>
          </section>

          <section>
            <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Last sync</h4>
            <DrawerRow label="Status">
              <span className={m.text}>{m.label}</span>
            </DrawerRow>
            <DrawerRow label="When">{fmtWhen(i.last_sync_at)}</DrawerRow>
            <DrawerRow label="Devices imported">{i.last_device_count ?? "—"}</DrawerRow>
            {i.poll_via_agent && (
              <DrawerRow label="Collected by">
                {i.last_collector_hostname || (
                  <span className="text-amber-600 dark:text-amber-400">
                    No agent has reported yet
                  </span>
                )}
              </DrawerRow>
            )}
            {i.last_sync_error && (
              <div className="mt-2">
                <div className="text-xs text-gray-500 dark:text-gray-400 mb-1">Sync error</div>
                <pre className="text-[11px] font-mono whitespace-pre-wrap break-words rounded-lg p-3 max-h-72 overflow-auto
                  bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 text-red-600 dark:text-red-400">
                  {i.last_sync_error}
                </pre>
              </div>
            )}
            <p className="text-[11px] text-gray-400 mt-2">
              Imported devices appear in the Discovered list above (each with its own status + detail).
            </p>
          </section>

          {hasConfig && (
            <section>
              <h4 className="text-xs font-semibold uppercase text-gray-400 mb-1">Configuration</h4>
              <pre className="text-[11px] font-mono whitespace-pre-wrap break-words rounded-lg p-3 max-h-60 overflow-auto
                bg-gray-50 dark:bg-gray-950 border border-gray-200 dark:border-gray-700 text-gray-800 dark:text-gray-200">
                {JSON.stringify(i.extra_config, null, 2)}
              </pre>
            </section>
          )}
        </div>
      </div>
    </>
  );
}

// ---------- Inline forms ----------

function DeployCredForm({
  customerId,
  existing,
  onClose,
  onSaved,
}: {
  customerId: number;
  existing: DeployCred | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [username, setUsername] = useState(existing?.username || "");
  const [domain, setDomain] = useState(existing?.domain || "");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setErr("");
    if (!username.trim() || !password) {
      setErr("Username and password are both required.");
      return;
    }
    setSaving(true);
    try {
      await api.put("/api/v1/network-discovery/deploy-credentials", {
        customer_id: customerId,
        username: username.trim(),
        domain: domain.trim() || null,
        password,
      });
      await onSaved();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" {...backdropClose(onClose)}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          {existing?.configured ? "Update" : "Set"} deploy credentials
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          The admin login an on-site agent uses to remotely install the agent on discovered
          Windows devices. Use a domain or local administrator with rights on the targets.
        </p>
        {err && <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 mt-2 p-2 rounded text-sm">{err}</div>}
        <div className="mt-3 space-y-3 text-sm">
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Username</span>
            <input
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="svc-deploy  (or  DOMAIN\\svc-deploy)"
              autoComplete="off"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            />
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Domain (optional)</span>
            <input
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              placeholder="CONTOSO — leave blank for a local account or if the username already has one"
              autoComplete="off"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            />
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Password</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={existing?.configured ? "Enter to replace the saved password" : ""}
              autoComplete="new-password"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            />
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded" disabled={saving}>
            Cancel
          </button>
          <button onClick={submit} disabled={saving} className="px-3 py-1.5 bg-brand text-white rounded disabled:opacity-50">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewTargetForm({
  customerId,
  customerDevices,
  onClose,
  onCreated,
}: {
  customerId: number;
  customerDevices: Device[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [cidr, setCidr] = useState("192.168.1.0/24");
  const [label, setLabel] = useState("");
  const [scannerId, setScannerId] = useState<number | "">("");
  const [interval, setInterval] = useState(1440);
  const [portscan, setPortscan] = useState(false);
  const [snmp, setSnmp] = useState(true);
  const [err, setErr] = useState("");

  const submit = async () => {
    setErr("");
    try {
      await api.post("/api/v1/network-discovery/targets", {
        customer_id: customerId,
        cidr,
        label: label || null,
        scanner_device_id: scannerId === "" ? null : scannerId,
        scan_interval_minutes: interval,
        portscan_enabled: portscan,
        snmp_autodetect_enabled: snmp,
        enabled: true,
      });
      await onCreated();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" {...backdropClose(onClose)}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add scan target</h3>
        {err && <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 mt-2 p-2 rounded text-sm">{err}</div>}
        <div className="mt-3 space-y-3 text-sm">
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">CIDR</span>
            <input value={cidr} onChange={(e) => setCidr(e.target.value)} className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600" />
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Label (optional)</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600" />
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Scanner device (optional)</span>
            <select
              value={scannerId}
              onChange={(e) => setScannerId(e.target.value === "" ? "" : Number(e.target.value))}
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            >
              <option value="">Auto-pick first online agent</option>
              {customerDevices.map((d) => (
                <option key={d.id} value={d.id}>{d.hostname} ({d.status})</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Scan interval (minutes)</span>
            <input type="number" value={interval} min={5} max={10080} onChange={(e) => setInterval(Number(e.target.value))} className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600" />
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={portscan} onChange={(e) => setPortscan(e.target.checked)} />
            <span>Light TCP port scan on responders (slower, more visible to IDS)</span>
          </label>
          <label className="flex items-center gap-2">
            <input type="checkbox" checked={snmp} onChange={(e) => setSnmp(e.target.checked)} />
            <span>SNMP autodetect (public/private community strings)</span>
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <ActionButton onAction={() => submit()} variant="raw" className="px-3 py-1.5 bg-brand text-white rounded" pendingLabel="Working…">Create</ActionButton>
        </div>
      </div>
    </div>
  );
}

function EditIntegrationForm({
  integration,
  vendors,
  onClose,
  onSaved,
}: {
  integration: Integration;
  vendors: Vendor[];
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const meta = vendors.find((v) => v.vendor === integration.vendor);
  const [name, setName] = useState(integration.name);
  const [baseUrl, setBaseUrl] = useState(integration.api_base_url || "");
  const [apiKey, setApiKey] = useState("");
  // Pre-populate extra_config values from the existing row so the user
  // sees what's stored and can correct it (e.g. wrong organization_id).
  const [extra, setExtra] = useState<Record<string, string>>(() => {
    const out: Record<string, string> = {};
    for (const [k, v] of Object.entries(integration.extra_config || {})) {
      out[k] = v == null ? "" : String(v);
    }
    return out;
  });
  const [enabled, setEnabled] = useState(integration.enabled);
  const [pollViaAgent, setPollViaAgent] = useState(!!integration.poll_via_agent);
  const [collectorDeviceId, setCollectorDeviceId] = useState<number | null>(
    integration.collector_device_id ?? null,
  );
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setErr("");
    setSaving(true);
    try {
      const body: Record<string, unknown> = {
        name: name || integration.name,
        api_base_url: baseUrl || null,
        extra_config: extra,
        enabled,
      };
      // ONLY send the collector fields when the user actually changed them.
      //
      // Sending them unconditionally caused a real regression: a save from a
      // browser running an older bundle (where integration.poll_via_agent was
      // undefined, so the checkbox initialised to false) silently turned relay
      // OFF for a customer whose controller is only reachable that way. The user
      // was editing the integration's NAME and lost its sync mode.
      //
      // The backend treats undefined as "leave alone", so omitting is safe and
      // makes a stale client incapable of clobbering a setting it never knew
      // about.
      if (pollViaAgent !== !!integration.poll_via_agent) {
        body.poll_via_agent = pollViaAgent;
      }
      if (
        pollViaAgent &&
        (collectorDeviceId ?? null) !== (integration.collector_device_id ?? null)
      ) {
        body.collector_device_id = collectorDeviceId;
      }
      // Only send api_key if the user typed a new one. Blank = keep existing.
      if (apiKey.trim()) body.api_key = apiKey.trim();
      await api.put(`/api/v1/network-integrations/${integration.id}`, body);
      await onSaved();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50"
      {...backdropClose(onClose)}
    >
      <div
        className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-lg p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">
          Edit integration — {meta?.label || integration.vendor}
        </h3>
        <p className="text-xs text-gray-500 mt-1">
          Customer: <strong>{integration.customer_name || `#${integration.customer_id}`}</strong>{" "}
          (can't change after creation — delete + re-add if you need to move it)
        </p>
        {err && (
          <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 mt-2 p-2 rounded text-sm">
            {err}
          </div>
        )}
        <div className="mt-3 space-y-3 text-sm">
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Name</span>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            />
          </label>
          {meta?.needs_base_url && (
            <label className="block">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Base URL</span>
              <input
                value={baseUrl}
                onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://..."
                className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              />
            </label>
          )}
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">
              API key / token / password
            </span>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={
                integration.has_api_key
                  ? "Leave blank to keep current key"
                  : "Paste API key"
              }
              autoComplete="new-password"
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            />
            {integration.has_api_key && (
              <span className="block text-[11px] text-gray-500 mt-1">
                A key is currently saved (encrypted). Leave blank to keep it; type
                a new one to rotate.
              </span>
            )}
            {meta?.credential_help && (
              <span className="block text-[11px] text-amber-700 dark:text-amber-400 mt-1 leading-snug">
                {meta.credential_help}
              </span>
            )}
          </label>
          <IntegrationCollector
            customerId={integration.customer_id}
            supported={integration.collector_supported !== false}
            vendorLabel={meta?.label || integration.vendor}
            pollViaAgent={pollViaAgent}
            collectorDeviceId={collectorDeviceId}
            onChange={(n) => {
              setPollViaAgent(n.poll_via_agent);
              setCollectorDeviceId(n.collector_device_id);
            }}
          />
          {(meta?.extra_fields || []).map((f) => (
            <label key={f.key} className="block">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">
                {f.label}
                {f.required ? " *" : ""}
              </span>
              <input
                value={extra[f.key] || ""}
                onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              />
              {f.help && (
                <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                  {f.help}
                </span>
              )}
            </label>
          ))}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
            />
            <span className="text-gray-700 dark:text-gray-300">Enabled</span>
          </label>
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded" disabled={saving}>
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={saving}
            className="px-3 py-1.5 bg-brand text-white rounded disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NewIntegrationForm({
  customerId,
  customerOptions,
  vendors,
  onClose,
  onCreated,
}: {
  customerId: number | null;
  customerOptions: { value: number; label: string }[];
  vendors: Vendor[];
  onClose: () => void;
  onCreated: () => Promise<void>;
}) {
  const [selectedCustomer, setSelectedCustomer] = useState<number | "">(
    customerId ?? "",
  );
  const [vendor, setVendor] = useState(vendors[0]?.vendor || "meraki");
  const [name, setName] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [err, setErr] = useState("");

  const meta = vendors.find((v) => v.vendor === vendor);

  const submit = async () => {
    setErr("");
    if (selectedCustomer === "") {
      setErr("Pick a customer for this integration.");
      return;
    }
    try {
      await api.post("/api/v1/network-integrations", {
        customer_id: selectedCustomer,
        vendor,
        name: name || meta?.label || vendor,
        api_key: apiKey || null,
        api_base_url: baseUrl || null,
        extra_config: extra,
        enabled: true,
        sync_interval_minutes: 60,
      });
      await onCreated();
    } catch (e: any) {
      setErr(e.response?.data?.detail || e.message);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center p-4 z-50" {...backdropClose(onClose)}>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">Add network integration</h3>
        {err && <div className="bg-red-50 border border-red-200 text-red-800 dark:bg-red-900/30 dark:border-red-800 dark:text-red-300 mt-2 p-2 rounded text-sm">{err}</div>}
        <div className="mt-3 space-y-3 text-sm">
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Customer</span>
            <select
              value={selectedCustomer}
              onChange={(e) =>
                setSelectedCustomer(e.target.value === "" ? "" : Number(e.target.value))
              }
              className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
            >
              <option value="">Pick a customer…</option>
              {customerOptions.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Vendor</span>
            <select value={vendor} onChange={(e) => { setVendor(e.target.value); setExtra({}); }} className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600">
              {vendors.map((v) => (
                <option key={v.vendor} value={v.vendor}>{v.label}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">Name</span>
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder={meta?.label} className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600" />
          </label>
          {meta?.needs_base_url && (
            <label className="block">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">Base URL</span>
              <input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)} placeholder="https://..." className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600" />
            </label>
          )}
          <label className="block">
            <span className="block text-gray-600 dark:text-gray-300 mb-1">API key / token / password</span>
            <input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)} className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600" />
            {meta?.credential_help && (
              <span className="block text-[11px] text-amber-700 dark:text-amber-400 mt-1 leading-snug">
                {meta.credential_help}
              </span>
            )}
          </label>
          {(meta?.extra_fields || []).map((f) => (
            <label key={f.key} className="block">
              <span className="block text-gray-600 dark:text-gray-300 mb-1">
                {f.label}
                {f.required ? " *" : ""}
              </span>
              <input
                value={extra[f.key] || ""}
                onChange={(e) => setExtra({ ...extra, [f.key]: e.target.value })}
                className="w-full border rounded px-3 py-2 dark:bg-gray-900 dark:border-gray-600"
              />
              {f.help && (
                <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
                  {f.help}
                </span>
              )}
            </label>
          ))}
        </div>
        <div className="mt-4 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 border rounded">Cancel</button>
          <ActionButton onAction={() => submit()} variant="raw" className="px-3 py-1.5 bg-brand text-white rounded" pendingLabel="Working…">Create</ActionButton>
        </div>
      </div>
    </div>
  );
}
